import React, {useCallback, useContext, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {mapDateFormat} from 'src/utils/mapDateFormat';

import {FlatButton} from '@pb/kit-component-button';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Scrollable} from '@pb/kit-component-scrollable';
import {Pill, StripOfPills} from '@pb/kit-component-strip-of-pills';
import {LocaleContext} from '@pb/kit-context-locale';

import {FILTER_STRIP_LABELS} from '../../constants';
import {ssoAccountsStateActions, ssoAccountsStateSelector} from '../../services/sso-accounts-state.slice';
import {formatBoolean, formatEnumValue} from '../../utils/format';

import {
    ssoAccountsFilterStripClearButton,
    ssoAccountsFilterStripGroup,
    ssoAccountsFilterStripPill,
} from './sso-accounts-filter-strip.module.styl';

import type {SsoAccountsFilters} from '../../models/typedef';

type Props = {
    clearAllDisabled: boolean,
};

export const SsoAccountsFilterStrip = ({clearAllDisabled}: Props) => {
    const dispatch = useDispatch();
    const cacheKeys = useSelector(ssoAccountsStateSelector);
    const {locale} = useContext(LocaleContext);

    const onRemovePill = useCallback((propName: keyof SsoAccountsFilters) => {
        const currentFilters = {...cacheKeys.filters};
        delete currentFilters[propName];

        dispatch(ssoAccountsStateActions.setFilters(currentFilters));
    }, [cacheKeys.filters, dispatch]);

    const clearCriteria = useCallback(() => {
        dispatch(ssoAccountsStateActions.setFilters({}));
        dispatch(ssoAccountsStateActions.setSearchCriteria({}));
    }, [dispatch]);

    const getPillValue = useCallback((labelKey: keyof SsoAccountsFilters, value: string | boolean) => {
        if (typeof value === 'boolean') {
            return formatBoolean(value);
        }

        if (
            labelKey === 'createdFrom' ||
            labelKey === 'createdTo' ||
            labelKey === 'finishedFrom' ||
            labelKey === 'finishedTo'
        ) {
            return FormattedDate.format({
                value: new Date(value),
                format: mapDateFormat(locale.dateFormat, 'NUMERIC'),
            }) || value;
        }

        return formatEnumValue(value);
    }, [locale.dateFormat]);

    const stripData = useMemo(() => Object.entries(cacheKeys.filters)
        .map(([key, value]) => {
            const labelKey = key as keyof SsoAccountsFilters;
            return {
                propName: labelKey,
                label: FILTER_STRIP_LABELS[labelKey] || key,
                visibleValue: getPillValue(labelKey, value as string | boolean),
            };
        }), [cacheKeys.filters, getPillValue]);

    const stripView = useMemo(() => stripData.map(({propName, label, visibleValue}) => (
        <StripOfPills key={propName} label={label}>
            <Pill
                className={ssoAccountsFilterStripPill}
                key={propName}
                name={visibleValue}
                removable={true}
                preventedRemoveHandler={() => onRemovePill(propName)}
            />
        </StripOfPills>
    )), [stripData, onRemovePill]);

    return (
        <span className={ssoAccountsFilterStripGroup}>
            <Scrollable>
                {stripView}
                <FlatButton
                    className={ssoAccountsFilterStripClearButton}
                    startIcon={<Icon type={Icons.TRASH}/>}
                    onClick={clearCriteria}
                    disabled={clearAllDisabled}>
                    Clear all criteria
                </FlatButton>
            </Scrollable>
        </span>
    );
};
